import type { AdminRole } from './auth.api.js';
import { apiFetch } from './client.js';

export interface AdminUser {
  id: string;
  email: string;
  roles: AdminRole[];
  /** Finer than a role (DEV-112): today only can_approve_edd. */
  permissions: string[];
  mfaRequired: boolean;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface CreateAdminUserInput {
  email: string;
  password: string;
  roles?: AdminRole[];
  mfaRequired?: boolean;
}

interface ListResponse { admins: AdminUser[] }
interface SingleResponse { admin: AdminUser }

export const adminUsersApi = {
  async list(): Promise<AdminUser[]> {
    const data = await apiFetch<ListResponse>('/admin/auth/admins');
    return data.admins;
  },

  async create(input: CreateAdminUserInput): Promise<AdminUser> {
    const data = await apiFetch<SingleResponse>('/admin/auth/admins', {
      method: 'POST',
      body: {
        email: input.email,
        password: input.password,
        roles: input.roles?.map((r) => r.toUpperCase()),
        mfaRequired: input.mfaRequired ?? true,
      },
    });
    return data.admin;
  },

  async setMfa(id: string, required: boolean): Promise<AdminUser> {
    const data = await apiFetch<SingleResponse>(`/admin/auth/admins/${id}/mfa`, {
      method: 'PATCH',
      body: { required },
    });
    return data.admin;
  },

  /** Change a staff member's role. Administrators only; nobody removes their own admin role. */
  async setRoles(id: string, roles: readonly string[]): Promise<AdminUser> {
    const data = await apiFetch<SingleResponse>(`/admin/auth/admins/${id}/roles`, {
      method: 'PATCH',
      body: { roles: roles.map((role) => role.toUpperCase()) },
    });
    return data.admin;
  },

  /**
   * Replace a staff member's permissions (DEV-112). Administrators only; the
   * server refuses a grant to yourself and records every change.
   */
  async setPermissions(id: string, permissions: readonly string[]): Promise<AdminUser> {
    const data = await apiFetch<SingleResponse>(`/admin/auth/admins/${id}/permissions`, {
      method: 'PATCH',
      body: { permissions: [...permissions] },
    });
    return data.admin;
  },
};
